import { Button, HighlightIcon, Icon } from '@dev-spendesk/grapes';
import { differenceInDays } from 'date-fns';
import illustration from 'public/static/img/internationalTransfers/illustration-kyc.svg';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUserRoles } from 'modules/app/hooks/useUserRoles';
import { useFetchKycRemediation } from 'modules/invoices/hooks/useFetchKycRemediation';
import { isEeaMember } from 'src/core/config/country';
import { LocalStorageKey } from 'src/core/constants/storage';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

export const InternationalWireTransferAccountValidationModal = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const isEEAOrEUROCurrency =
    company.currency === 'EUR' || isEeaMember(company.country);
  const { isAccountOwner } = useUserRoles();
  const lastClosed = localStorage.getItem(
    LocalStorageKey.IwtRemediateModalLastDisplayDate,
  );
  const wasAlreadyClosedRecently = lastClosed
    ? differenceInDays(new Date(), new Date(lastClosed)) < 7
    : false;
  const fetchKycRemediationResult = useFetchKycRemediation({
    isEnabled:
      isEEAOrEUROCurrency && isAccountOwner && !wasAlreadyClosedRecently,
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (
      fetchKycRemediationResult.status === 'success' &&
      fetchKycRemediationResult.data.status === 'draft'
    ) {
      setIsOpen(true);
    }
  }, [fetchKycRemediationResult]);

  const closeModal = () => {
    setIsOpen(false);
    localStorage.setItem(
      LocalStorageKey.IwtRemediateModalLastDisplayDate,
      new Date().toISOString(),
    );
  };

  const trackDoItLater = () => {
    track(
      AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_KYC_REMEDIATION_SKIPPED,
      { source: 'Page Modal' },
    );
  };

  const openKycAndCloseModal = () => {
    // should always be true here because we don't show the modal if the status is not 'success'
    if (fetchKycRemediationResult.status === 'success') {
      window.open(fetchKycRemediationResult.data.onboardingUrl, '_blank');
      track(
        AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_KYC_REMEDIATION_OPENED,
        { source: 'Page Modal' },
      );
    }
    closeModal();
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[9001] flex h-full justify-center bg-background-primary-default">
      <div className="flex h-full flex-col justify-center overflow-auto">
        <div className="absolute left-32 top-20 flex items-center">
          <img
            height="24"
            src="/static/img/spendesk-black-logo.svg"
            alt="spendeskLogo"
          />
          <span className="ml-16 text-content-secondary-bg-primary body-m">
            |&nbsp;
            {t(
              'international-wiretransfer.accountValidationModal.complianceCheck',
            )}
          </span>
        </div>
        <div className="flex w-[60%] min-w-[450px] flex-col justify-center self-center p-32">
          <HighlightIcon name="triangle-warning" size={56} variant="warning" />
          <h1 className="mt-32 heading-xxl">
            {t('international-wiretransfer.accountValidationModal.title')}
          </h1>
          <p className="mt-16 flex items-center gap-4 text-content-success-default body-m">
            <Icon name="clock-filled" />
            {t('international-wiretransfer.accountValidationModal.time')}
          </p>
          <p className="mt-32 text-content-primary body-m">
            {t('international-wiretransfer.accountValidationModal.description')}
          </p>
          <Button
            className="mt-32"
            text={t(
              'international-wiretransfer.accountValidationModal.reviewCta',
            )}
            fit="parent"
            variant="primaryBrand"
            onClick={openKycAndCloseModal}
          />
          <i className="mt-8 text-center text-content-secondary-bg-secondary">
            {t(
              'international-wiretransfer.accountValidationModal.dotFileRedirection',
            )}
          </i>
          <Button
            className="mt-16"
            text={t('misc.doThisLater')}
            variant="tertiaryNeutral"
            onClick={() => {
              closeModal();
              trackDoItLater();
            }}
          />
        </div>
      </div>
      <div className="flex h-full w-[50%] items-center justify-center p-40">
        <img
          src={illustration}
          alt="illustration"
          className="max-h-full w-full max-w-[900px]"
        />
      </div>
    </div>,
    document.body,
  );
};
